import React, { useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function AuthorsTable({ authorView, setAuthorView, filteredAuthors }) {
  const [onlyPnu, setOnlyPnu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const coAuthorRef = useRef(null);

  // PNU 필터링
  const isPnuInstitution = (name = '') => {
    const n = String(name || '').toLowerCase();
    return n.includes('부산대학교') || n.includes('pusan national university') || n.includes('pnu');
  };

  const displayAuthors = useMemo(() => {
    if (!Array.isArray(filteredAuthors)) return [];
    return onlyPnu ? filteredAuthors.filter((r) => isPnuInstitution(r.institution)) : filteredAuthors;
  }, [filteredAuthors, onlyPnu]);

  // Author Role 매핑 및 분류
  const categorizeByRole = (author) => {
    const role = String(author.author_role || '').trim();
    if (role === '단독저자' || role === '제1저자' || role === 'First Author' || role === 'Single Author') {
      return 'first';
    } else if (role === '공동(참여)' || role === 'Co-author' || role === '공동저자') {
      return 'co';
    } else {
      return 'corresponding';
    }
  };

  // Role별로 분류
  const authorsByRole = useMemo(() => {
    const first = [];
    const co = [];
    const corresponding = [];

    displayAuthors.forEach((author) => {
      const category = categorizeByRole(author);
      if (category === 'first') {
        first.push(author);
      } else if (category === 'co') {
        co.push(author);
      } else {
        corresponding.push(author);
      }
    });

    return { first, co, corresponding };
  }, [displayAuthors]);

  // 기관별로 그룹화
  const groupByInstitution = (authors) => {
    const grouped = {};
    authors.forEach((author) => {
      const inst = author.institution || 'Unknown';
      if (!grouped[inst]) {
        grouped[inst] = [];
      }
      grouped[inst].push(author);
    });
    return grouped;
  };

  // PNU 저자 렌더링 (프로필 이미지, 이름, ID 마크 팝업 포함)
  const renderPnuAuthor = (author, idx) => {
    // scholar_id 또는 author_id 중 사용 가능한 것 사용 (0도 유효한 값이므로 명시적 체크)
    const scholarId = (author.scholar_id != null && author.scholar_id !== '' && author.scholar_id !== '0')
      ? author.scholar_id
      : (author.author_id != null && author.author_id !== '' && author.author_id !== '0')
        ? author.author_id
        : null;
    const uniqueKey = `author-${scholarId || author.author_name || idx}-${idx}`;
    const profileImageUrl = (author.profile_pic && String(author.profile_pic).trim() !== '')
      ? author.profile_pic
      : (scholarId
        ? `${rmVars.themeUrl}images/profile.png`
        : `${rmVars.themeUrl}images/no-profile-gy.png`);

    // ID 정보 추출
    const ids = Array.isArray(author.research_ids) ? author.research_ids : [];
    const findId = (t) => {
      try {
        const it = ids.find(r => r?.id_type === t);
        return it?.id_value || '';
      } catch (e) {
        return '';
      }
    };
    // ID 값 추출 (빈 문자열과 null 체크)
    const orcid = (author.orcid && String(author.orcid).trim()) || (findId('orcid') && String(findId('orcid')).trim()) || '';
    const researcherId = (author.researcher_id && String(author.researcher_id).trim()) || (findId('researcher_id') && String(findId('researcher_id')).trim()) || '';
    const googleId = (author.googlescholar_id && String(author.googlescholar_id).trim()) || (findId('googlescholar_id') && String(findId('googlescholar_id')).trim()) || (findId('google_scholar_id') && String(findId('google_scholar_id')).trim()) || '';
    const isni = (author.isni && String(author.isni).trim()) || (findId('isni') && String(findId('isni')).trim()) || '';

    const orcidUrl = orcid ? `https://orcid.org/${orcid}` : '';
    const researcherIdUrl = researcherId ? `https://www.researcherid.com/rid/${researcherId}` : '';
    const googleUrl = googleId ? `https://scholar.google.com/citations?user=${encodeURIComponent(googleId)}` : '';
    const isniUrl = isni ? `https://nl.go.kr/isni/${isni}` : '';
    const pnuUrl = scholarId != null ? `/researchers/${encodeURIComponent(scholarId)}` : '';

    const authorName = author.author_name || '-';
    // scholar_id 또는 author_id가 있고, ORCID/ResearcherID/Google Scholar/ISNI 중 하나라도 있으면 ID 마크 표시
    const hasId = (scholarId != null) && (orcid || researcherId || googleId || isni);

    return (
      <div className="flex items-center gap-1 cursor-pointer shrink-0 whitespace-nowrap">
        <div className="shrink-0 w-6 h-6 overflow-hidden rounded-full border border-[#E4E8F1]">
          <img
            src={profileImageUrl}
            alt="profile photo"
            onError={(e) => {
              e.target.src = `${rmVars.themeUrl}images/no-profile-gy.png`;
            }}
          />
        </div>
        {scholarId != null ? (
          <Link to={pnuUrl} className="hover:underline">
            {authorName}
          </Link>
        ) : (
          <span>{authorName}</span>
        )}

        {/* ID 마크 및 팝업 */}
        {hasId && (
          <div className="group relative">
            <img
              src={`${rmVars.themeUrl}images/id-mark.png`}
              className="id-mark w-[18px] h-[18px]"
              alt="id mark"
            />
            {/* 마크 호버시 팝업 - 상단에 투명 패딩 추가로 브릿지 역할 */}
            <div className="id-hover flex hidden absolute bottom-[calc(100%+15px)] left-[50%] translate-x-[-50%] flex-col gap-[6px] bg-white border border-[#E4E8F1] shadow-[0px_4px_20px_rgba(0,0,0,0.1)] rounded-[12px] pt-[20px] pb-4 px-4 after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0 after:border-l-[8px] after:border-l-transparent after:border-r-[8px] after:border-r-transparent after:border-t-[8px] after:border-t-[#E4E8F1] before:content-[''] before:absolute before:top-[calc(100%-1px)] before:left-1/2 before:-translate-x-1/2 before:w-0 before:h-0 before:border-l-[7px] before:border-l-transparent before:border-r-[7px] before:border-r-transparent before:border-t-[7px] before:border-t-white after:z-0 before:z-[1] group-hover:flex hover:flex" style={{ pointerEvents: 'auto' }}>
              {pnuUrl && (
                <div className="flex flex-col gap-[6px] w-full h-full before: content-[''] before: absolute before:bottom-[-24px] before:left-0 before:w- [192px]" style={{ pointerEvents: 'auto' }}>
                  <Link to={pnuUrl} className="flex items-center gap-[5px]">
                    <img
                      src={`${rmVars.themeUrl}images/pnu-page.png`}
                      className="w-[21px] h-[21px]"
                      alt="pnu icon"
                    />
                    <span className="text-[12px] text-[#376BDB] font-semibold">PNU Scholar Page</span>
                  </Link>
                </div>
              )}
              {orcidUrl && (
                <div className="relative w-[158px] bg-[#F3F5F9] rounded-[12px] p-[6px]" style={{ pointerEvents: 'auto' }}>
                  <a href={orcidUrl} target="_blank" rel="noreferrer" className="flex items-center gap-[5px]">
                    <img
                      src={`${rmVars.themeUrl}images/orcid-page.png`}
                      className="w-[21px] h-[21px]"
                      alt="orcid icon"
                    />
                    <span className="text-[12px] text-[#555] font-semibold">ORCID</span>
                    <img
                      src={`${rmVars.themeUrl}images/launch-ico-555.png`}
                      className="absolute right-[6px] top-[50%] translate-y-[-50%] w-[13px] h-[13px]"
                      alt="link"
                    />
                  </a>
                </div>
              )}
              {researcherIdUrl && (
                <div className="relative w-[158px] bg-[#F3F5F9] rounded-[12px] p-[6px]" style={{ pointerEvents: 'auto' }}>
                  <a href={researcherIdUrl} target="_blank" rel="noreferrer" className="flex items-center gap-[5px]">
                    <img
                      src={`${rmVars.themeUrl}images/wos-page.png`}
                      className="w-[21px] h-[21px]"
                      alt="WoS ID icon"
                    />
                    <span className="text-[12px] text-[#555] font-semibold">WoS ID</span>
                    <img
                      src={`${rmVars.themeUrl}images/launch-ico-555.png`}
                      className="absolute right-[6px] top-[50%] translate-y-[-50%] w-[13px] h-[13px]"
                      alt="link"
                    />
                  </a>
                </div>
              )}
              {googleUrl && (
                <div className="relative flex items-center gap-[5px] w-[158px] bg-[#F3F5F9] rounded-[12px] p-[6px]" style={{ pointerEvents: 'auto' }}>
                  <a href={googleUrl} target="_blank" rel="noreferrer" className="flex items-center gap-[5px]">
                    <img
                      src={`${rmVars.themeUrl}images/googlescholar-page.png`}
                      className="w-[21px] h-[21px]"
                      alt="google scholar icon"
                    />
                    <span className="text-[12px] text-[#555] font-semibold">Google Scholar</span>
                    <img
                      src={`${rmVars.themeUrl}images/launch-ico-555.png`}
                      className="absolute right-[6px] top-[50%] translate-y-[-50%] w-[13px] h-[13px]"
                      alt="link"
                    />
                  </a>
                </div>
              )}
              {isniUrl && (
                <div className="relative flex items-center gap-[5px] w-[158px] bg-[#F3F5F9] rounded-[12px] p-[6px]" style={{ pointerEvents: 'auto' }}>
                  <a href={isniUrl} target="_blank" rel="noreferrer" className="flex items-center gap-[5px]">
                    <img
                      src={`${rmVars.themeUrl}images/isni-page.png`}
                      className="w-[21px] h-[21px]"
                      alt="isni icon"
                    />
                    <span className="text-[12px] text-[#555] font-semibold">ISNI</span>
                    <img
                      src={`${rmVars.themeUrl}images/launch-ico-555.png`}
                      className="absolute right-[6px] top-[50%] translate-y-[-50%] w-[13px] h-[13px]"
                      alt="link"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Role 섹션 렌더링 (새로운 HTML 구조)
  const renderRoleSection = (title, authors, roleKey, isLast = false) => {
    if (!authors || authors.length === 0) return null;

    const isCoAuthor = roleKey === 'co';

    // 기관별로 전체 그룹화
    const allGrouped = groupByInstitution(authors);
    // 전체 기관 순서 추출
    const allOrderedInstitutions = [];
    const allSeenInstitutions = new Set();
    authors.forEach(a => {
      const inst = a.institution || 'Unknown';
      if (!allSeenInstitutions.has(inst)) {
        allOrderedInstitutions.push([inst, allGrouped[inst]]);
        allSeenInstitutions.add(inst);
      }
    });

    const showMoreButton = isCoAuthor && allOrderedInstitutions.length > 5;
    // 5개 기관까지만 노출 (펼쳐지지 않았을 때)
    const displayInstitutions = isCoAuthor && !isExpanded
      ? allOrderedInstitutions.slice(0, 5)
      : allOrderedInstitutions;

    return (
      <div ref={roleKey === 'co' ? coAuthorRef : null}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[16px] font-semibold leading-[100%]">{title}</p>
        </div>
        <ul className="flex flex-col gap-4 p-4 lg:p-6 text-[14px] text-[#6C7687] leading-[140%] border border-[#E4E8F1] rounded-[12px]">
          {displayInstitutions.map(([institution, instAuthors], idx) => {
            const isPnu = isPnuInstitution(institution);
            return (
              <li key={`${roleKey}-${institution}-${idx}`} className="leading-[140%] min-w-0 break-words">
                {/* 기관명 + 저자명: 길면 2줄·3줄 자동 줄바꿈 */}
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={`font-bold shrink-0 ${isPnu ? 'text-[#376BDB]' : ''}`}>{institution}</span>
                  {isPnu ? (
                    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] text-[#6C7687] align-baseline">
                      {instAuthors.map((author, authorIdx) => (
                        <React.Fragment key={`${roleKey}-${institution}-author-${author.author_id || authorIdx}-${authorIdx}`}>
                          {renderPnuAuthor(author, authorIdx)}
                        </React.Fragment>
                      ))}
                    </span>
                  ) : (
                    <span className="break-words">
                      {instAuthors.map((author, authorIdx) => (
                        <React.Fragment key={`${roleKey}-${institution}-author-${author.author_id || authorIdx}-${authorIdx}`}>
                          <span>{author.author_name || '-'}</span>
                          {authorIdx < instAuthors.length - 1 && <span className="mx-1">;</span>}
                        </React.Fragment>
                      ))}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        {showMoreButton && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => {
                const wasExpanded = isExpanded;
                setIsExpanded(!isExpanded);
                if (wasExpanded) {
                  // 접을 때는 상태 변경 후 약간의 지연을 주어 레이아웃이 변경된 후 스크롤
                  setTimeout(() => {
                    coAuthorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }
              }}
              className="flex items-center justify-center gap-2 w-full py-3 bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px] text-[14px] text-[#6C7687] font-semibold hover:bg-[#EEF1F8] transition-colors"
            >
              <span>{isExpanded ? 'Show Less' : `More (+${allOrderedInstitutions.length - 5})`}</span>
              <img
                src={`${rmVars.themeUrl}images/expand_more_333.png`}
                className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                style={{ filter: 'invert(48%) sepia(8%) saturate(675%) hue-rotate(182deg) brightness(93%) contrast(89%)' }}
                alt="expand"
              />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-[50px] lg:mt-[80px]">
      <div className="flex items-center justify-between mb-5 lg:mb-6">
        <h2 className="text-[18px] font-bold lg:text-[20px]">
          Author(s)
        </h2>
        <label className="checkbox-wrap group inline-flex ml-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyPnu}
            onChange={(e) => setOnlyPnu(e.target.checked)}
            className="peer hidden"
          />
          <span
            className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] group-hover:border-[#4B4DFF] transition-colors duration-200 translate-y-[3px] bg-no-repeat bg-[length:cover] bg-[position:center]"
            style={onlyPnu ? {
              backgroundImage: `url('${rmVars.themeUrl}images/checked.png')`
            } : {}}
          >
          </span>
          <span className="ml-2 text-[14px] text-[#6C7687] font-normal">PNU Researchers Only</span>
        </label>
      </div>

      {displayAuthors.length === 0 ? (
        <div className="text-center py-6 text-[#8D9AAF]">No Result</div>
      ) : (
        <div className="flex flex-col gap-4 lg:gap-6">
          {(() => {
            const sections = [
              { title: 'First Author(s)', authors: authorsByRole.first, key: 'first' },
              { title: 'Co-Author(s)', authors: authorsByRole.co, key: 'co' },
              { title: 'Corresponding Author(s)', authors: authorsByRole.corresponding, key: 'corresponding' }
            ].filter(s => s.authors && s.authors.length > 0);

            return sections.map((section, idx) => (
              <React.Fragment key={section.key}>
                {renderRoleSection(section.title, section.authors, section.key, idx === sections.length - 1)}
              </React.Fragment>
            ));
          })()}
        </div>
      )}
    </div>
  );
}
